import React, { useState, useCallback, useRef, useEffect } from 'react';
import DocsLayout, { CodeBlock } from 'Containers/MCP/docs/DocsLayout';

const PATH = '/mcp/docs/skills';

const SKILL_CATEGORIES = [
  {
    id: 'onboarding',
    label: 'Getting Started',
    skills: [
      {
        name: 'setup-brand',
        title: 'Set up your brand',
        description: 'First-time brand setup wizard — walks through brand selection, content import, and voice training.',
        when: 'You\'re new to Vocable or want to configure a new brand.',
        prompt: 'Use Vocable to set up my brand — import my content and train my voice',
        chains: ['list_brands', 'set_default_brand', 'auto_import_blogs', 'fetch_social_posts', 'analyze_voice_dna'],
      },
      {
        name: 'import-content',
        title: 'Import your content',
        description: 'Bulk import blogs and social posts from Twitter, LinkedIn, Instagram, YouTube, and TikTok to train your brand voice.',
        when: 'You want to add training data from your existing content.',
        prompt: 'Use Vocable to import content from vocable.ai and my socials @vocableai',
        chains: ['auto_import_blogs', 'fetch_social_posts', 'list_content_samples'],
      },
    ],
  },
  {
    id: 'planning',
    label: 'Planning',
    skills: [
      {
        name: 'campaign-planner',
        title: 'Plan a campaign',
        description: 'Go from a campaign goal to a fully populated plan — keyword research, topic ideation, briefs, and headlines.',
        when: 'You want to plan a content campaign from scratch.',
        prompt: 'Use Vocable to plan a content campaign about AI in marketing for Q4, targeting LinkedIn and blog',
        chains: ['create_campaign', 'keyword_research', 'ideate_content', 'generate_brief', 'generate_headlines'],
      },
      {
        name: 'ideation',
        title: 'Ideate topics',
        description: 'Generate content ideas backed by keyword data and cross-referenced against your existing content library.',
        when: 'You need topic inspiration grounded in real data.',
        prompt: 'Use Vocable to find content ideas about content marketing',
        chains: ['keyword_research', 'semantic_search', 'ideate_content'],
      },
      {
        name: 'editorial-calendar',
        title: 'Build an editorial calendar',
        description: 'Create a structured content plan for a week or month across multiple channels.',
        when: 'You want to fill out a content calendar with briefs.',
        prompt: 'Use Vocable to build me a 2-week editorial calendar for blog, LinkedIn, and Twitter',
        chains: ['list_campaigns', 'create_brief', 'generate_brief'],
      },
    ],
  },
  {
    id: 'creation',
    label: 'Creating',
    skills: [
      {
        name: 'draft',
        title: 'Draft content',
        description: 'Write content in your trained brand voice. Loads voice DNA, generates, saves as document, and runs a health check.',
        when: 'You want to create a piece of content.',
        prompt: 'Use Vocable to draft a LinkedIn post about why brand voice matters in AI-generated content',
        chains: ['generate_content', 'create_document', 'check_document_health'],
      },
      {
        name: 'repurpose',
        title: 'Repurpose content',
        description: 'Turn one piece of content into versions for LinkedIn, Twitter, Instagram, email, and more.',
        when: 'You have a blog post and want it adapted for other channels.',
        prompt: 'Use Vocable to repurpose my latest blog post into a LinkedIn post and Twitter thread',
        chains: ['get_document', 'generate_content', 'create_document'],
      },
      {
        name: 'review',
        title: 'Review before publishing',
        description: 'Health check, voice match, guideline compliance, and revision — comprehensive quality gate.',
        when: 'Content is ready and you want to verify quality before publishing.',
        prompt: 'Use Vocable to review document 42 before I publish it',
        chains: ['get_document', 'check_document_health', 'update_document'],
      },
    ],
  },
  {
    id: 'publishing',
    label: 'Publishing',
    skills: [
      {
        name: 'publish',
        title: 'Publish to channels',
        description: 'Publish or schedule to WordPress, LinkedIn, X, Facebook, Webflow, or Wix — with a quality check first.',
        when: 'Content is finalized and ready to go live.',
        prompt: 'Use Vocable to publish document 42 to my LinkedIn account',
        chains: ['list_integrations', 'check_document_health', 'publish_document'],
      },
      {
        name: 'batch-publish',
        title: 'Batch publish',
        description: 'Publish multiple documents to their channels in one session.',
        when: 'You have several pieces ready and want to push them all out.',
        prompt: 'Use Vocable to publish all my approved documents',
        chains: ['list_documents', 'list_integrations', 'publish_document'],
      },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    skills: [
      {
        name: 'brand-audit',
        title: 'Brand audit',
        description: 'Full brand analysis — voice DNA, content insights, strategy assessment, and actionable recommendations.',
        when: 'You want to understand your brand\'s content fingerprint and find gaps.',
        prompt: 'Use Vocable to run a full brand audit',
        chains: ['run_full_analysis', 'get_brand_insights'],
      },
      {
        name: 'research',
        title: 'Content research',
        description: 'Keyword research, content library search, and opportunity discovery — cross-referenced against your brand.',
        when: 'You want to find what to write about next.',
        prompt: 'Use Vocable to research content opportunities around "brand intelligence"',
        chains: ['keyword_research', 'semantic_search', 'find_similar_content', 'get_brand_insights'],
      },
      {
        name: 'voice-check',
        title: 'Voice consistency check',
        description: 'Compare content against your trained brand voice — tone, vocabulary, style, and guideline compliance.',
        when: 'You want to make sure content sounds like your brand.',
        prompt: 'Use Vocable to check if document 42 matches my brand voice',
        chains: ['get_document'],
      },
    ],
  },
  {
    id: 'collaboration',
    label: 'Collaboration',
    skills: [
      {
        name: 'share-and-assign',
        title: 'Share with team',
        description: 'Share documents, brands, or campaigns with teammates.',
        when: 'You need to give someone access to content.',
        prompt: 'Use Vocable to share my latest blog post with sarah@company.com as editor',
        chains: ['list_team_members', 'share_document', 'share_brand', 'add_campaign_collaborator'],
      },
      {
        name: 'team-status',
        title: 'Pipeline status',
        description: 'Overview of what\'s in progress, what needs attention, and what\'s ready to publish.',
        when: 'You want a status update on your content pipeline.',
        prompt: 'Use Vocable to show me my team\'s content pipeline status',
        chains: ['list_campaigns', 'list_briefs', 'list_documents', 'list_team_members'],
      },
    ],
  },
  {
    id: 'meta',
    label: 'Meta',
    skills: [
      {
        name: 'status',
        title: 'Connection status',
        description: 'Quick health check — active brand, training status, rate limits.',
        when: 'You want to verify your Vocable connection is working.',
        prompt: 'Check my Vocable status',
        chains: ['list_brands', 'rate_limit_status'],
      },
      {
        name: 'help',
        title: 'What can I do?',
        description: 'See all available workflows and how to use them.',
        when: 'You\'re not sure what to ask.',
        prompt: 'What can I do with the Vocable connector?',
        chains: [],
      },
    ],
  },
];

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [text]);

  return (
    <button
      type="button"
      className="mcp-docs__copy-btn"
      onClick={handleCopy}
    >
      {copied ? 'Copied!' : 'Copy prompt'}
    </button>
  );
};

const SkillCard = ({ skill }) => (
  <div className="mcp-docs__skill-card">
    <h3 id={skill.name}>{skill.title}</h3>
    <p>{skill.description}</p>
    <div className="mcp-docs__skill-when">
      <strong>When to use:</strong> {skill.when}
    </div>
    <div className="mcp-docs__skill-prompt">
      <div className="mcp-docs__skill-prompt-header">
        <span>Example prompt</span>
        <CopyButton text={skill.prompt} />
      </div>
      <code>{skill.prompt}</code>
    </div>
    {skill.chains.length > 0 && (
      <div className="mcp-docs__skill-chains">
        <span>Tools used: </span>
        {skill.chains.map((tool, i) => (
          <span key={tool}>
            <code className="mcp-docs__code-inline">{tool}</code>
            {i < skill.chains.length - 1 && ' → '}
          </span>
        ))}
      </div>
    )}
  </div>
);

const Skills = () => (
  <DocsLayout path={PATH}>
    <div className="mcp-docs__hero">
      <p className="mcp-docs__eyebrow">Workflows</p>
      <h1>Skills &amp; Prompts</h1>
      <p>
        17 ready-made workflows that chain Vocable's tools together into the
        jobs you actually do. Each one is a complete workflow — you describe
        what you want, and the agent handles the rest.
      </p>
    </div>

    <h2>How to use</h2>
    <p>
      You don't need to memorize these. Just describe what you want in plain
      language — "plan a campaign about AI marketing" or "draft a LinkedIn
      post about our new feature" — and Claude will use the right workflow
      automatically.
    </p>
    <p>
      The prompts below are shortcuts. Copy any of them into Claude to kick
      off that specific workflow.
    </p>

    {SKILL_CATEGORIES.map((category) => (
      <section key={category.id}>
        <h2 id={category.id}>{category.label}</h2>
        <div className="mcp-docs__skill-grid">
          {category.skills.map((skill) => (
            <SkillCard key={skill.name} skill={skill} />
          ))}
        </div>
      </section>
    ))}

    <h2>All 17 workflows at a glance</h2>
    <div className="mcp-docs__table-wrap">
      <table className="mcp-docs__table">
        <thead>
          <tr>
            <th>Workflow</th>
            <th>What it does</th>
            <th>Tools chained</th>
          </tr>
        </thead>
        <tbody>
          {SKILL_CATEGORIES.flatMap((cat) =>
            cat.skills.map((skill) => (
              <tr key={skill.name}>
                <td><strong>{skill.title}</strong></td>
                <td>{skill.description}</td>
                <td>{skill.chains.length || '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </DocsLayout>
);

export default Skills;
